import React, { useRef, useState, useEffect, useCallback, useId } from 'react';
import { COLORS } from '../constants/theme';

export interface CardCarouselProps<T> {
  items: T[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderCard: (item: T, isSelected: boolean, isFocused: boolean) => React.ReactNode;
  ariaLabel: string;
  getItemId: (item: T) => string;
}

export function CardCarousel<T>({
  items,
  selectedId,
  onSelect,
  renderCard,
  ariaLabel,
  getItemId,
}: CardCarouselProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // activeIndex tracks the navigation cursor — updated only by explicit user actions
  // (button clicks, keyboard, card clicks) so it never races with scroll animations.
  const initialIndex = selectedId
    ? Math.max(items.findIndex((item) => getItemId(item) === selectedId), 0)
    : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const announcerRef = useRef<HTMLDivElement>(null);
  const liveId = useId();

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  const scrollCardIntoView = useCallback(
    (index: number, behavior: ScrollBehavior = scrollBehavior) => {
      cardRefs.current[index]?.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'start',
      });
    },
    [scrollBehavior]
  );

  // On mount: scroll selected card into view instantly.
  useEffect(() => {
    if (initialIndex > 0) {
      scrollCardIntoView(initialIndex, 'auto');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only on mount

  const navigate = useCallback(
    (next: number) => {
      setActiveIndex(next);
      scrollCardIntoView(next);
    },
    [scrollCardIntoView]
  );

  // Keyboard navigation within the radiogroup.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const current = focusedIndex ?? activeIndex;
      let next: number | null = null;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next = Math.min(current + 1, items.length - 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        next = Math.max(current - 1, 0);
      } else if (e.key === 'Home') {
        e.preventDefault();
        next = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        next = items.length - 1;
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const id = getItemId(items[current]);
        onSelect(id);
        return;
      }

      if (next !== null) {
        setFocusedIndex(next);
        cardRefs.current[next]?.focus();
        navigate(next);
      }
    },
    [focusedIndex, activeIndex, items, getItemId, onSelect, navigate]
  );

  // Announce selection changes to screen readers.
  useEffect(() => {
    if (!selectedId || !announcerRef.current) return;
    const item = items.find((i) => getItemId(i) === selectedId);
    if (!item) return;
    announcerRef.current.textContent = `Selected: ${getItemId(item)}`;
  }, [selectedId, items, getItemId]);

  const atStart = activeIndex === 0;
  const atEnd = activeIndex === items.length - 1;

  return (
    <div className="flex flex-col gap-1">
      {/* Screen reader live region */}
      <div
        ref={announcerRef}
        id={liveId}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      <div className="flex items-center gap-1">
        {/* Previous arrow */}
        <button
          type="button"
          onClick={() => navigate(Math.max(activeIndex - 1, 0))}
          aria-label={`Previous ${ariaLabel}`}
          aria-disabled={atStart}
          disabled={atStart}
          style={{
            minWidth: 44,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            color: atStart ? COLORS.text.muted : COLORS.text.secondary,
            cursor: atStart ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        >
          ‹
        </button>

        {/* Scroll container */}
        <div
          ref={scrollRef}
          role="radiogroup"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (focusedIndex === null) {
              const sel = selectedId
                ? items.findIndex((i) => getItemId(i) === selectedId)
                : -1;
              setFocusedIndex(sel !== -1 ? sel : 0);
            }
          }}
          onBlur={() => setFocusedIndex(null)}
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'scroll',
            scrollSnapType: 'x mandatory',
            scrollBehavior: prefersReducedMotion ? 'auto' : 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            gap: 8,
            flex: 1,
            outline: 'none',
          }}
          className="[&::-webkit-scrollbar]:hidden"
        >
          {items.map((item, index) => {
            const id = getItemId(item);
            const isSelected = id === selectedId;
            const isFocused = focusedIndex === index;

            return (
              <div
                key={id}
                ref={(el) => { cardRefs.current[index] = el; }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={-1}
                onClick={() => {
                  onSelect(id);
                  setActiveIndex(index);
                }}
                onFocus={() => setFocusedIndex(index)}
                style={{
                  scrollSnapAlign: 'start',
                  flexShrink: 0,
                  cursor: 'pointer',
                  outline: 'none',
                  border: `2px solid ${isSelected ? COLORS.link : COLORS.border}`,
                  borderRadius: 8,
                  backgroundColor: isSelected ? 'rgba(106, 158, 142, 0.12)' : COLORS.card,
                  boxShadow: isFocused ? `0 0 0 2px ${COLORS.link}` : undefined,
                }}
                // Width must be in className only — inline style would override responsive variants
                className="w-[calc(85%_-_4px)] sm:w-[calc(50%_-_4px)] md:w-[calc(33.333%_-_6px)] lg:w-[calc(25%_-_6px)]"
              >
                {renderCard(item, isSelected, isFocused)}
              </div>
            );
          })}
        </div>

        {/* Next arrow */}
        <button
          type="button"
          onClick={() => navigate(Math.min(activeIndex + 1, items.length - 1))}
          aria-label={`Next ${ariaLabel}`}
          aria-disabled={atEnd}
          disabled={atEnd}
          style={{
            minWidth: 44,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            color: atEnd ? COLORS.text.muted : COLORS.text.secondary,
            cursor: atEnd ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        >
          ›
        </button>
      </div>

      <span className="sr-only">
        {ariaLabel} {activeIndex + 1} of {items.length}
      </span>
    </div>
  );
}
